import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const comparisonRows = [
  { factor: 'Primary Goal', seo: 'Rank in Google search results', geo: 'Be cited & recommended by AI engines' },
  { factor: 'Target Platforms', seo: 'Google Search, Bing', geo: 'ChatGPT, Google Gemini, Perplexity, AI Overviews' },
  { factor: 'Result Format', seo: 'List of 10 blue links', geo: 'One confident recommendation by name' },
  { factor: 'Core Method', seo: 'Keyword optimisation, backlinks, domain authority', geo: 'Authority signals, structured answer content, entity clarity' },
  { factor: 'Success Metric', seo: 'Rankings, impressions, organic clicks', geo: 'AI citations, brand mention frequency, share of voice' },
  { factor: 'Competition Level', seo: 'Saturated — thousands of pages competing', geo: 'First-mover advantage — most businesses have not started' },
  { factor: 'Correlation', seo: 'Was 70% correlated with GEO results', geo: 'Now below 20% — completely separate disciplines' },
  { factor: 'Timeline to Results', seo: '3–6 months to rank competitively', geo: '2–4 months to establish AI authority' },
];

export default function SeoVsGeoSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="seo-vs-geo" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-border to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />
      <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-ds-red/4 blur-[160px] rounded-full pointer-events-none -translate-y-1/2" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16">
          <span className="section-label reveal">Two Different Games</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-none reveal">
              YOU NEED TO
              <br />
              <span className="text-ds-red neon-text-red">WIN BOTH.</span>
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-md reveal">
              Ranking well on Google no longer guarantees you appear in AI answers. The overlap between top Google results and AI-cited sources has dropped from 70% to below 20%.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Definitive statement — AI-scrapable */}
        <div className="border border-ds-red/20 bg-ds-bg-2 p-8 mb-12 reveal">
          <p className="font-body text-white/80 text-sm leading-relaxed">
            <strong className="text-white">The definitive difference between SEO and GEO:</strong> SEO optimises for search engine algorithms. GEO optimises for AI answer generation. SEO gets you a position in a list. GEO gets you named as the answer. Mission Beach businesses need both — but most only have SEO. That is why 47% of your competitors are invisible to AI search.
          </p>
        </div>

        {/* Comparison table */}
        <div className="border border-ds-border overflow-hidden reveal">
          {/* Table header */}
          <div className="grid grid-cols-3 bg-ds-bg-2 border-b border-ds-border">
            <div className="px-6 py-4 font-display text-xs text-white/30 tracking-widest uppercase">Factor</div>
            <div className="px-6 py-4 font-display text-xs text-white/50 tracking-widest uppercase border-l border-ds-border">
              Traditional SEO
            </div>
            <div className="px-6 py-4 font-display text-xs tracking-widest uppercase border-l border-ds-red/60 bg-ds-red/5 text-ds-red">
              GEO / AEO
            </div>
          </div>
          {/* Table rows */}
          {comparisonRows.map((row, i) => (
            <div
              key={row.factor}
              className={`grid grid-cols-3 border-b border-ds-border last:border-b-0 group hover:bg-ds-bg-2 transition-colors duration-200 ${i % 2 === 0 ? 'bg-ds-bg' : 'bg-ds-bg-2/50'}`}
            >
              <div className="px-6 py-4 font-display text-xs text-white/40 tracking-wider uppercase flex items-center">
                {row.factor}
              </div>
              <div className="px-6 py-4 font-body text-sm text-white/50 border-l border-ds-border flex items-center leading-snug">
                {row.seo}
              </div>
              <div className="px-6 py-4 font-body text-sm text-white border-l border-ds-red/30 bg-ds-red/3 flex items-center gap-2 leading-snug group-hover:text-white transition-colors duration-200">
                <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-ds-red text-xs" />
                </div>
                {row.geo}
              </div>
            </div>
          ))}
        </div>

        {/* Opportunity callout */}
        <div className="mt-10 flex flex-col md:flex-row gap-6 reveal">
          <div className="flex-1 border border-ds-border bg-ds-bg-2 p-6 text-center">
            <div className="font-display text-5xl text-ds-red neon-text-red mb-2">47%</div>
            <p className="font-body text-white/50 text-sm">of brands currently have no GEO strategy at all</p>
          </div>
          <div className="flex-1 border border-ds-red bg-ds-bg-2 p-6 text-center" style={{ boxShadow: '0 0 30px rgba(180,255,57,0.08)' }}>
            <div className="font-display text-4xl text-white mb-2">Mission Beach</div>
            <p className="font-body text-ds-red text-sm tracking-wider uppercase">First-Mover Advantage</p>
            <p className="font-body text-white/40 text-xs mt-2 leading-relaxed">
              Be the first Mission Beach business in your category to establish AI authority. The window is open now.
            </p>
          </div>
          <div className="flex-1 border border-ds-border bg-ds-bg-2 p-6 text-center">
            <div className="font-display text-5xl text-ds-red neon-text-red mb-2">&lt;20%</div>
            <p className="font-body text-white/50 text-sm">overlap between Google top results and AI-cited sources</p>
          </div>
        </div>

      </div>
    </section>
  );
}
